// BUILD-042 (Oct 20 final UAT batch) - the COMPACT one-person access editor rows, the saved-access summary and the collapsed "About HR access" help.
//
// PRESENTATION ONLY. The permissions, their valid scopes, the checkbox/scope state, "Save changes", the stale-screen (409) protection, the no-self rule, the Owner-only
// rule, atomicity and auditing all stay in AccessPanel (HrAdminTab.tsx) and on the server exactly as before; nothing here grants, revokes or decides anything. These
// pieces only draw what AccessPanel already knows, in less space:
//   * one row per permission: checkbox - short label - scope (a dropdown only where there is a real choice, otherwise quiet text) - an expand arrow for plain-language details;
//   * one summary row per person, with badges for the permissions they ACTUALLY hold (a permission nobody was given is never drawn) and an expander for scope and whether
//     it is a saved setting or built in;
//   * a collapsed "About HR access" section for the rules that used to sit in the middle of the page.
import { useId, useState } from 'react';
import type { HrAccessGrantRow } from '../../../api/client';
import {
  EDITABLE_PERMISSIONS, capabilityLabel, editableScopeLabel, editableShortLabel, editableShortScopeLabel, scopeLabel,
  type AccessScope, type EditablePermission,
} from './accessLabels';

const SCOPE_RANK: Record<AccessScope, number> = { self: 0, direct_reports: 1, company: 2 };

function Chevron({ open }: { open: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The widest scope among a person's stored rows for one capability (the server treats the widest as the effective one), or null when they hold none. */
export function widestScope(rows: readonly HrAccessGrantRow[], capability: string): AccessScope | null {
  const mine = rows.filter((g) => g.capability === capability);
  if (mine.length === 0) return null;
  return mine.reduce<AccessScope>((w, g) => (SCOPE_RANK[g.scope as AccessScope] > SCOPE_RANK[w] ? (g.scope as AccessScope) : w), mine[0].scope as AccessScope);
}

// ── one permission row ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
export function PermissionRow({ perm, on, scope, heldScope, locked, saving, ownerOnlyLocked, onToggle, onScope }: {
  perm: EditablePermission;
  /** The checkbox as the screen shows it (saved state plus any unsaved edit). */
  on: boolean;
  scope: AccessScope;
  /** What the person holds today (saved), used only to keep an unusual saved reach visible in the dropdown. */
  heldScope: AccessScope | null;
  /** Checkbox and dropdown are not changeable (their own access, or an Owner-only permission for a non-Owner). */
  locked: boolean;
  saving: boolean;
  /** This is the Owner-only permission and the viewer is not the Primary Owner. */
  ownerOnlyLocked: boolean;
  onToggle: (on: boolean) => void;
  onScope: (scope: AccessScope) => void;
}) {
  const [open, setOpen] = useState(false);
  const uid = useId();
  const short = editableShortLabel(perm.capability);
  const boxId = `${uid}-box`;
  const detailId = `${uid}-detail`;
  const unusual = heldScope !== null && !perm.scopes.includes(heldScope);
  const options: AccessScope[] = unusual && heldScope ? [heldScope, ...perm.scopes] : [...perm.scopes];
  return (
    <li className="py-2" data-testid={`perm-${perm.capability}`}>
      <div className="flex items-center gap-3 min-h-[2rem]">
        <input id={boxId} type="checkbox" className="h-4 w-4 flex-shrink-0" checked={on} disabled={locked || saving} onChange={(e) => onToggle(e.target.checked)} />
        <label htmlFor={boxId} className="text-sm font-medium text-gray-900 flex-1 min-w-0">{short}</label>
        <div className="flex-shrink-0 min-w-[9.5rem] text-right">
          {on && (options.length > 1 ? (
            <select
              className="border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100 disabled:text-gray-500"
              aria-label={`${short}: who it covers`} value={scope} disabled={locked || saving}
              onChange={(e) => onScope(e.target.value as AccessScope)}
            >
              {options.map((s) => <option key={s} value={s}>{editableShortScopeLabel(perm.capability, s)}</option>)}
            </select>
          ) : (
            <span className="text-xs text-gray-500" data-testid={`perm-scope-${perm.capability}`}>{editableShortScopeLabel(perm.capability, scope)}</span>
          ))}
        </div>
        <button
          type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={detailId}
          aria-label={`${open ? 'Hide' : 'Show'} details for ${short}`} data-testid={`perm-expand-${perm.capability}`}
          className="flex-shrink-0 p-1 -mr-1 rounded text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
        >
          <Chevron open={open} />
        </button>
      </div>
      {ownerOnlyLocked && <p className="ml-7 text-[11px] text-gray-500">Only the Primary Owner can change this.</p>}
      {open && (
        <div id={detailId} className="ml-7 mr-7 mt-1 mb-1 text-xs text-gray-600 space-y-1" data-testid={`perm-detail-${perm.capability}`}>
          <p>{perm.meaning}</p>
          {on && <p className="text-gray-500">Covers: {editableScopeLabel(perm.capability, scope)}.</p>}
          {unusual && heldScope && <p className="text-amber-700">Held today at an unusual reach: {editableScopeLabel(perm.capability, heldScope)}. Pick a standard one, or leave it as is.</p>}
        </div>
      )}
    </li>
  );
}

// ── the saved summary: one row per person ──────────────────────────────────────────────────────────────────────────────────────────────
interface HeldPermission { capability: string; scope: AccessScope; source: 'saved' | 'builtin'; editable: boolean }
interface PersonAccess { employeeId: string; held: HeldPermission[]; others: HrAccessGrantRow[]; builtIn: boolean }

/** Group the stored rows by person into the permissions each ACTUALLY holds. `builtInOwnerId` (the Primary Owner, known only when the viewer is the Owner) holds every permission, built in. */
export function summarizeAccess(rows: readonly HrAccessGrantRow[], builtInOwnerId: string | null): PersonAccess[] {
  const byPerson = new Map<string, HrAccessGrantRow[]>();
  for (const g of rows) { const list = byPerson.get(g.employeeId) ?? []; list.push(g); byPerson.set(g.employeeId, list); }
  if (builtInOwnerId && !byPerson.has(builtInOwnerId)) byPerson.set(builtInOwnerId, []);
  const editableCodes = EDITABLE_PERMISSIONS.map((p) => p.capability);
  const people: PersonAccess[] = [];
  for (const [employeeId, list] of byPerson) {
    const builtIn = employeeId === builtInOwnerId;
    const held: HeldPermission[] = [];
    for (const p of EDITABLE_PERMISSIONS) {
      if (builtIn) { held.push({ capability: p.capability, scope: 'company', source: 'builtin', editable: true }); continue; }
      const scope = widestScope(list, p.capability);
      if (scope) held.push({ capability: p.capability, scope, source: 'saved', editable: true });
    }
    const others = list.filter((g) => !editableCodes.includes(g.capability));
    people.push({ employeeId, held, others, builtIn });
  }
  // The built-in Owner first, then everyone else in the order the server returned them.
  return people.sort((a, b) => Number(b.builtIn) - Number(a.builtIn));
}

export function AccessSummary({ rows, builtInOwnerId, viewerEmployeeId, personName, onEdit }: {
  rows: readonly HrAccessGrantRow[];
  builtInOwnerId: string | null;
  viewerEmployeeId: string;
  personName: (employeeId: string) => string;
  onEdit: (employeeId: string) => void;
}) {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const people = summarizeAccess(rows, builtInOwnerId);
  const toggle = (id: string) => setOpenIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (people.length === 0) return <div className="text-sm text-gray-400 py-2">No one has been given HR access yet.</div>;
  return (
    <ul className="divide-y divide-gray-100" data-testid="access-overview">
      {people.map((p) => {
        const open = openIds.has(p.employeeId);
        const detailId = `access-person-detail-${p.employeeId}`;
        const name = personName(p.employeeId);
        const isYou = p.employeeId === viewerEmployeeId;
        return (
          <li key={p.employeeId} className="py-2" data-testid={`access-person-${p.employeeId}`}>
            <div className="flex items-center gap-2">
              <button
                type="button" onClick={() => toggle(p.employeeId)} aria-expanded={open} aria-controls={detailId}
                aria-label={`${open ? 'Hide' : 'Show'} access details for ${name}`} data-testid={`access-expand-${p.employeeId}`}
                className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
              >
                <Chevron open={open} />
              </button>
              <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-sm font-medium text-gray-900">{name}{isYou ? ' (you)' : ''}</span>
                <span className="flex flex-wrap gap-1" aria-label={`Permissions held by ${name}`}>
                  {p.held.map((h) => (
                    <span key={h.capability} className="text-[11px] rounded px-1.5 py-0.5 bg-[#EDF5FC] text-[#1d6fa5]" data-testid={`access-badge-${p.employeeId}-${h.capability}`}>{editableShortLabel(h.capability)}</span>
                  ))}
                  {p.others.map((g) => (
                    <span key={g.grantId} className="text-[11px] rounded px-1.5 py-0.5 bg-gray-100 text-gray-600">{capabilityLabel(g.capability)}</span>
                  ))}
                  {p.builtIn && <span className="text-[11px] rounded px-1.5 py-0.5 bg-gray-100 text-gray-600">Built in</span>}
                </span>
              </div>
              {p.builtIn && !isYou ? null : (
                <button type="button" onClick={() => onEdit(p.employeeId)} className="text-xs text-[#4AABDE] hover:underline flex-shrink-0" data-testid={`access-open-${p.employeeId}`}>
                  {isYou ? 'View' : 'Edit access'}
                </button>
              )}
            </div>
            {open && (
              <ul id={detailId} className="ml-9 mt-1 mb-1 space-y-0.5 text-xs text-gray-600" data-testid={`access-detail-${p.employeeId}`}>
                {p.held.length === 0 && p.others.length === 0 && <li>No saved permissions.</li>}
                {p.held.map((h) => (
                  <li key={h.capability}>
                    <span className="font-medium text-gray-700">{editableShortLabel(h.capability)}</span>
                    {' · '}{editableScopeLabel(h.capability, h.scope)}
                    {' · '}{h.source === 'builtin' ? 'Built in with the Primary Owner role (cannot be changed here)' : 'Saved setting'}
                  </li>
                ))}
                {p.others.map((g) => (
                  <li key={g.grantId}>
                    <span className="font-medium text-gray-700">{capabilityLabel(g.capability)}</span>{' · '}{scopeLabel(g.scope)}{' · '}Saved setting (not changed on this screen)
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── collapsed help ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
export function AboutHrAccess() {
  return (
    <details className="text-xs text-gray-500 mt-4" data-testid="about-hr-access">
      <summary className="text-[#4AABDE] cursor-pointer">About HR access</summary>
      <div className="mt-2 max-w-lg space-y-1.5">
        <p><strong className="font-medium text-gray-700">Their own record.</strong> Every employee with an HR record can already see their own HR record automatically. That is not a saved permission, so it is not listed on this page.</p>
        <p><strong className="font-medium text-gray-700">Who a permission covers.</strong> Each permission also has a reach: only themselves, the people who report to them, or the whole company. Only the reaches that make sense for a permission are offered, and some are whole company only.</p>
        <p><strong className="font-medium text-gray-700">Restrictions.</strong> Only authorized access administrators can change permissions, and nobody can change their own access. Only the Primary Owner can change Manage access. Primary Owner authority is built in and is not a saved setting. Confidential compartment access stays Primary-Owner-only; HR management does not include it or pay access.</p>
        <p><strong className="font-medium text-gray-700">Saving.</strong> Save changes applies everything you changed for this person at once, or nothing. If someone else changed their access while you were editing, nothing is saved and you are shown the latest.</p>
      </div>
    </details>
  );
}
